import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}`;

const Settings = () => {
    // State for the toggle, loading, and saving status
    const [isBonusModeOn, setIsBonusModeOn] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');

    // Fetch the current settings from the backend when the component loads
    const fetchSettings = useCallback(async () => {
        setIsLoading(true);
        setError('');
        try {
            const response = await axios.get(`${API_BASE_URL}/api/v1/admin/bonus-setting`);
            // Assuming the backend returns { isBonusModeActive: true/false }
            console.log("🚀 ~ fetchSettings ~ response:", response)
            setIsBonusModeOn(response.data?.data?.isBonusModeActive);
        } catch (err) {
            console.error("Failed to fetch settings:", err);
            setError(err.response?.data?.message || 'Could not load settings. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    // Handle the toggle change
    const handleToggleChange = async () => {
        if (isSaving || isLoading) return; // Prevent multiple clicks while saving/loading

        const newSettingValue = !isBonusModeOn;
        setIsSaving(true);
        setIsBonusModeOn(newSettingValue); // Optimistically update the UI

        try {
            // Send the new setting to the backend
            await axios.put(`${API_BASE_URL}/api/v1/admin/update-bonus-setting`, {
                isProvideBonusLesson: newSettingValue
            });
            toast.success(`Bonus Lessons are now ${newSettingValue ? 'ON' : 'OFF'}`);
        } catch (err) {
            console.error("Failed to update settings:", err);
            toast.error('Failed to save setting. Reverting change.');
            setIsBonusModeOn(!newSettingValue); // Revert UI on failure
            setError(err.response?.data?.message || 'Could not save setting.');
        } finally {
            setIsSaving(false);
        }
    };

    // A simple toggle switch component using Tailwind CSS
    const ToggleSwitch = ({ enabled, onChange, disabled }) => (
        <button
            type="button"
            onClick={onChange}
            disabled={disabled}
            className={`${enabled ? 'bg-blue-600' : 'bg-gray-200'}
                relative inline-flex items-center h-6 rounded-full w-11 transition-colors
                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                disabled:opacity-50 disabled:cursor-not-allowed`}
        >
            <span
                className={`${enabled ? 'translate-x-6' : 'translate-x-1'}
                    inline-block w-4 h-4 transform bg-white rounded-full transition-transform`}
            />
        </button>
    );

    return (
        <div className="p-4 md:p-6 bg-gray-50 h-full">
            <div className="">
                {/* Header */}
                <h1 className="text-2xl md:text-3xl font-semibold text-gray-800 mb-6 ">
                    System Settings
                </h1>

                {isLoading ? (
                    <div className="text-center p-10 bg-white rounded-lg shadow-md">
                        <p className="text-gray-500">Loading Settings...</p>
                    </div>
                ) : error ? (
                    <div className="p-4 bg-red-100 text-red-700 border border-red-300 rounded-md">
                        {error}
                    </div>
                ) : (
                    // Settings Card
                    <div className="bg-white shadow-md rounded-lg overflow-hidden">
                        <div className="p-6">
                            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
                                {/* Left Side: Title and Description */}
                                <div className="flex-1 mb-4 sm:mb-0 sm:mr-6">
                                    <h2 className="text-lg font-semibold text-gray-900">Include Bonus Lessons</h2>
                                    <p className="text-sm text-gray-600 mt-1">
                                        Control whether SmartStart AI includes the "Bonus Lessons" in its recommendations.
                                    </p>
                                    
                                </div>
                                {/* Right Side: Toggle Switch */}
                                <div className="flex items-center space-x-4">
                                    <span className={`font-bold text-lg ${isBonusModeOn ? 'text-blue-600' : 'text-gray-500'}`}>
                                        {isBonusModeOn ? 'ON' : 'OFF'}
                                    </span>
                                    <ToggleSwitch
                                        enabled={isBonusModeOn}
                                        onChange={handleToggleChange}
                                        disabled={isSaving}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default Settings;